/**
 * DataSourceRow
 *
 * Unified row component for all connector types (OAuth, API key, webhook, native).
 * Replaces ConnectorCard + OAuthConnectorCard with a single consistent component.
 */

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { BrandIcon, BRAND_ICONS } from '@/components/ui/brand-icons'

export type DataSourceStatus = 'connected' | 'expired' | 'error' | 'disconnected' | 'webhook' | 'coming-soon' | 'active'

interface DataSourceRowProps {
  name: string
  description: string
  brand: keyof typeof BRAND_ICONS
  status: DataSourceStatus
  /** Pill label override (e.g. "Receiving data" for the tracker) */
  statusLabel?: string
  /** Subtitle override — email, domain, last sync, etc. */
  subtitle?: string
  /** Primary action label override */
  actionLabel?: string
  /** Whether the row is clickable */
  onClick?: () => void
  /** Secondary action (e.g. Reconnect for expired OAuth) */
  onSecondaryAction?: () => void
  secondaryActionLabel?: string
  isLoading?: boolean
  className?: string
}

type StatusConfig = {
  label: string
  kind: 'connected' | 'caution' | 'error' | 'muted' | 'badge'
}

const STATUS_CONFIG: Record<DataSourceStatus, StatusConfig | null> = {
  connected: { label: 'Connected', kind: 'connected' },
  active: { label: 'Active', kind: 'connected' },
  expired: { label: 'Needs reconnecting', kind: 'caution' },
  error: { label: 'Error', kind: 'error' },
  webhook: { label: 'Webhook', kind: 'muted' },
  disconnected: null,
  'coming-soon': { label: 'Coming soon', kind: 'badge' },
}

function getDefaultActionLabel(status: DataSourceStatus): string {
  switch (status) {
    case 'connected':
    case 'active':
      return 'Manage'
    case 'expired':
    case 'error':
      return 'Reconnect'
    case 'webhook':
      return 'Set up'
    case 'coming-soon':
      return ''
    default:
      return 'Connect'
  }
}

/** Paper pill: 20px, canvas tint, state dot + ink label. Caution keeps the warning tint. */
function StatusIndicator({ config }: { config: StatusConfig }) {
  const tone =
    config.kind === 'caution'
      ? { pill: 'bg-warning-tint text-warning', dot: 'bg-warning' }
      : config.kind === 'error'
        ? { pill: 'bg-danger-tint text-danger', dot: 'bg-danger' }
        : config.kind === 'connected'
          ? { pill: 'bg-canvas text-foreground', dot: 'bg-live' }
          : { pill: 'bg-muted text-foreground/70', dot: null }
  return (
    <span className={cn('inline-flex h-5 shrink-0 items-center gap-[5px] rounded-md px-1.5 text-xs font-medium leading-4', tone.pill)}>
      {tone.dot && <span className={cn('size-1.5 rounded-full', tone.dot)} />}
      {config.label}
    </span>
  )
}

export function DataSourceRow({
  name,
  description,
  brand,
  status,
  statusLabel,
  subtitle,
  actionLabel,
  onClick,
  onSecondaryAction,
  secondaryActionLabel,
  isLoading = false,
  className,
}: DataSourceRowProps) {
  const baseConfig = STATUS_CONFIG[status]
  const statusConfig = baseConfig && statusLabel ? { ...baseConfig, label: statusLabel } : baseConfig
  const isComingSoon = status === 'coming-soon'
  const defaultLabel = actionLabel ?? getDefaultActionLabel(status)
  // An expired OAuth row already carries "Reconnect" as its secondary action; the row action opens the sheet
  const resolvedActionLabel = onSecondaryAction && secondaryActionLabel === defaultLabel ? 'Manage' : defaultLabel
  const displaySubtitle = subtitle || description
  // Paper: the one action that fixes a broken connection is ink; everything else is outline.
  const needsFix = status === 'expired' || status === 'error'

  return (
    <div
      className={cn(
        'group flex items-center gap-3.5 px-4 py-4 sm:px-[18px]',
        !isComingSoon && 'cursor-pointer transition-colors hover:bg-muted/40',
        className
      )}
      onClick={!isComingSoon ? onClick : undefined}
    >
      <div className="flex size-9 shrink-0 items-center justify-center rounded-[9px] bg-card shadow-[0_0_0_1px_rgb(0_0_0/0.08)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]">
        <BrandIcon brand={brand} className={cn('size-5 object-contain', isComingSoon && 'opacity-60')} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('text-sm font-semibold leading-[18px]', isComingSoon ? 'text-foreground/50' : 'text-foreground')}>{name}</span>
          {statusConfig && <StatusIndicator config={statusConfig} />}
        </div>
        <p className={cn('line-clamp-1 text-xs leading-4', isComingSoon ? 'text-foreground/45' : 'text-foreground/60')}>
          {displaySubtitle}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {onSecondaryAction && secondaryActionLabel && (
          <Button
            variant="outline"
            className={cn(
              'h-[34px] rounded-lg px-[11px] text-xs font-medium shadow-none',
              needsFix ? 'border-transparent bg-foreground text-background hover:bg-foreground/90 hover:text-background' : 'border-input bg-background',
            )}
            disabled={isLoading}
            onClick={(e) => {
              e.stopPropagation()
              onSecondaryAction()
            }}
          >
            {secondaryActionLabel}
          </Button>
        )}

        {!isComingSoon && resolvedActionLabel && (
          <Button
            variant="outline"
            className={cn(
              'h-[34px] rounded-lg px-[11px] text-xs font-medium shadow-none',
              needsFix && !onSecondaryAction
                ? 'border-transparent bg-foreground text-background hover:bg-foreground/90 hover:text-background'
                : 'border-input bg-background',
            )}
            disabled={isLoading}
            onClick={(e) => {
              e.stopPropagation()
              onClick?.()
            }}
          >
            {resolvedActionLabel}
          </Button>
        )}
      </div>
    </div>
  )
}
